import classNames from 'classnames'
import { DefineComponentProps } from 'utils'
import type { Component } from 'vue'
import './Tabs.scss'

type TabSlotItem = {
  type: {
    name: string
  }
  props: TabPaneProps
  children: {
    default: () => VNode | Component
  }
}

export type TabsProps = {
  onTabChange?: (name: TabPaneProps['name']) => void
  lineWidth?: number
  lineHeight?: number
  horizontalGap?: number
  lineColor?: string
  tabHeight?: number
  tabNavHeight?: number | string
  modelValue?: any
  headerRight?: () => VNode | Component | null
  showDivider?: boolean
  initialIndex?: number
  onTabClick?: (name: TabPaneProps['name']) => void
  defaultItemWidth?: number[]
}

export const Tabs = defineComponent<TabsProps>({
  name: 'Tabs',
  props: DefineComponentProps<TabsProps>([
    'onTabChange',
    'onTabClick',
    'lineWidth',
    'lineHeight',
    'horizontalGap',
    'lineColor',
    'tabHeight',
    'tabNavHeight',
    'modelValue',
    'headerRight',
    'showDivider',
    'initialIndex',
    'defaultItemWidth',
  ]),

  emits: ['update:modelValue'],
  setup(props, { slots, emit }) {
    const currentIndex = ref(props.initialIndex || 0)
    const transformX = ref(0)
    const itemWidthList = ref<number[]>(props.defaultItemWidth || [])
    const tabRef = ref<HTMLDivElement>()

    const { lineWidth = 20, lineHeight = 2, horizontalGap = 20, lineColor = '', tabHeight = 30, tabNavHeight = 30 } = props
    const showDivider = computed(() => {
      return props.showDivider
    })

    const updateItemWidthList = () => {
      nextTick(() => {
        const items = tabRef.value?.querySelectorAll('.tabs-header-item')
        const newWidths: number[] = []
        items?.forEach((item) => {
          newWidths.push(item.getBoundingClientRect().width)
        })
        const next = newWidths.length === 0 || newWidths.every((w) => w === 0) ? props.defaultItemWidth || [] : newWidths
        // 仅在确有变化时写入，避免触发 onUpdated → 死循环
        const same = next.length === itemWidthList.value.length && next.every((w, i) => w === itemWidthList.value[i])
        if (!same) {
          itemWidthList.value = next
        }
        updateTransformX()
      })
    }

    onMounted(() => {
      updateItemWidthList()
    })

    const updateTransformX = () => {
      if (itemWidthList.value.length > 0) {
        const width = itemWidthList.value[currentIndex.value]
        const next = itemWidthList.value.slice(0, currentIndex.value).reduce((acc, curr) => acc + curr, 0) + width / 2 - lineWidth / 2 + currentIndex.value * horizontalGap
        if (transformX.value !== next) {
          transformX.value = next
        }
      }
    }

    // 仅在 modelValue / 当前 tab 切换时重算，不挂 onUpdated 避免死循环
    watch(
      () => [props.modelValue, currentIndex.value],
      () => {
        updateItemWidthList()
      },
    )

    return () => {
      const defaultSlots: TabSlotItem[] = ((slots.default?.() as unknown as TabSlotItem[]) || [])
        .map((item) => {
          if (item.type.name === 'TabPane') {
            return item
          }
          return item.children as unknown as TabSlotItem[]
        })
        .flat()
        .filter(Boolean)
      const slotPropsList: TabPaneProps[] = (defaultSlots.map((item) => item.props as TabPaneProps) || []).filter(Boolean)

      const styleVars: Record<string, string> = {
        '--tabs-line-width': `${lineWidth}px`,
        '--tabs-line-height': `${lineHeight}px`,
        '--tabs-horizontal-gap': `${horizontalGap}px`,
        '--tabs-line-color': lineColor || 'var(--colors-text-base)',
        '--tabs-tab-height': `${tabHeight}px`,
        '--tabs-tab-nav-height': typeof tabNavHeight === 'string' ? tabNavHeight : `${tabNavHeight}px`,
        '--tabs-divider-color': showDivider.value ? 'var(--colors-divider)' : 'transparent',
      }

      return (
        <div class={classNames('tabs-layout-comp')} style={styleVars}>
          <div class="tabs-header-container">
            <div class="tabs-header" ref={tabRef}>
              {slotPropsList.map((item, index) => (
                <div
                  class={classNames('tabs-header-item', {
                    active: currentIndex.value === index,
                  })}
                  key={item.name}
                  onClick={() => {
                    props.onTabClick?.(item.name)
                    if (currentIndex.value === index) {
                      return
                    }
                    currentIndex.value = index
                    emit('update:modelValue', item.name)
                    props.onTabChange?.(item.name)
                  }}
                >
                  {item.label}
                </div>
              ))}

              {itemWidthList.value.length > 0 && <div class="line" style={{ transform: `translate3d(${transformX.value}px, 0, 0)` }}></div>}
            </div>
            {props.headerRight && <div class="header-right">{props.headerRight()}</div>}
          </div>
          {itemWidthList.value.length > 0 && (
            <div class="tabs-content">
              {defaultSlots.map((item, index) => {
                if (item.type.name === 'TabPane') {
                  return (
                    <div
                      class={classNames('tabs-content-item', {
                        show: currentIndex.value === index,
                      })}
                    >
                      {item.children?.default?.()}
                    </div>
                  )
                }
                return null
              })}
            </div>
          )}
        </div>
      )
    }
  },
})

export interface TabPaneProps {
  label: string
  name: string | number
}

export const TabPane = defineComponent<TabPaneProps>({
  name: 'TabPane',
  props: DefineComponentProps<TabPaneProps>(['label', 'name']),
  setup() {
    return () => <div class="tab-pane-layout"></div>
  },
})
